import { FC } from 'react';
import Layout from '@/components/layout/Layout';
import Pagination from '@/components/ui/pagination/Pagination';
import Breadcrumbs from '@/components/ui/breadcrumbs/Breadcrumbs';
import BookCardItem from '@/components/ui/book-card-item/BookCardItem';
import SectionTitle from '@/components/ui/section-title/SectionTitle';
import CheckBoxFreeBook from '@/components/ui/checkbox-free-book/CheckBoxFreeBook';
import { useGetUrlPath } from '@/hooks/useGetUrlPath';
import { BOOKS_FOR_PAGE } from '@/helpers/variables';
import { IGenreBooksPage } from '@/interfaces/genreBooksPage.interface';
import { getFirstLetterCaps } from '@/helpers/helpers';
import styles from './Genres.module.scss';
import { IGenre } from '@/interfaces/genre.interface';
import Link from 'next/link';
import BlogBooks from '@/components/ui/blog-books/BlogBooks';

interface IGenreProps {
  books: IGenreBooksPage;
  genres: IGenre;
  freeBooks?: boolean;
}

const Genre: FC<IGenreProps> = ({ books, genres, freeBooks }) => {
  const genreName = getFirstLetterCaps(books.title);
  const genreAlias = books.alias;
  const pagesCount = Math.ceil(books?.books.total_count / BOOKS_FOR_PAGE);
  const metaPageNumber = `${books.books.page > 1 ? ` Страница ${books.books.page}` : ``}`;

  //Seo block
  const SEO_TITLE = `Книги жанра ${books.title}: скачать бесплатно или читать онлайн в электронной библиотеке Fantasy Worlds${metaPageNumber}`;
  const SEO_DESCRIPTION = `Все книги жанра «${genreName}». ▶ Вы можете скачать бесплатно и читать без регистрации книги 📚 в электронной библиотеке Fantasy Worlds.${metaPageNumber}`;
  const SEO_CANONICAL = `/genre/${genreAlias}`;

  const getBookBreadcrumbs = () => {
    return {
      __html: `{
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {"@type": "ListItem","position": 1,"name": "Главная","item": "https://fantasy-worlds.ru"},
        {"@type": "ListItem","position": 2,"name": "Жанры","item": "https://fantasy-worlds.ru/genre"},
        {"@type": "ListItem","position": 3,"name": "${genreName}","item": "https://fantasy-worlds.ru/genre/${genreAlias}/"}
       ]
    }`,
    };
  };

  return (
    <Layout title={SEO_TITLE} description={SEO_DESCRIPTION} canonicalHref={SEO_CANONICAL} pageHref={useGetUrlPath()} robotsNoFollow={books.books.data.length < 2} genreMenu>
      <Breadcrumbs firstPageName="Жанры" firstPageAlias="/genre/" secondPageName={genreName} />
      <script type="application/ld+json" dangerouslySetInnerHTML={getBookBreadcrumbs()} key="product-jsonld" />
      <Pagination defaultCurrentPage={books.books.page} pagesCount={pagesCount ?? 0} postName={freeBooks ? `genre/${genreAlias}/?free_books=true&page=` : `genre/${genreAlias}/`} />
      <SectionTitle name={`Книги жанра ${books.title}`} />
      <div className={styles.genres}>
        {!!genres && genres.child.length > 0 && (
          <div className={styles.genres__menu}>
            <div className={styles.genres__subGenres}>
              <div className={styles.genres__title}>Поджанры:</div>
              <ul className={styles.genres__list}>
                {genres.child.map((genre) => (
                  <li key={genre.id} className={styles.genres__item}>
                    <Link prefetch={false} className={styles.genres__link} href={`/genre/${genre.alias}`}>
                      {genre.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        <CheckBoxFreeBook freeBooks={!!freeBooks} alias={`/genre/${genreAlias}`} />
        {books?.books.data.length ? (
          books?.books.data.map((book, i) => {
            return (
              <BookCardItem
                createdAt={book.created_at}
                id={book.id}
                rating={book.rating}
                key={`${book.id}-${i}`}
                author={book.author}
                hideLitresData={book.hide_litres_data}
                comments={book.comment_count}
                draft={book.draft}
                title={book.title}
                type={book.type}
                description={book.description}
                genres={book.genres}
                allow_full_free={book.allow_full_free}
                external_id={book.external_id}
              />
            );
          })
        ) : (
          <div>Книги не найдены</div>
        )}
      </div>
      <Pagination defaultCurrentPage={books.books.page} pagesCount={pagesCount ?? 0} postName={freeBooks ? `genre/${genreAlias}/?free_books=true&page=` : `genre/${genreAlias}/`} />
      {/* <BlogBooks title="Блоги связанные с жанром" /> */}
    </Layout>
  );
};

export default Genre;
